"use client";

// B3.3 MARKETPLACE SUPPLIER DETAIL — WO-GF-001 Phase-1b, real machinery for the supplier-profile
// (ProfileCenter) system. Sibling of MarketplaceBrowse (B3.2). CONTENT-REGION REPLACEMENT: the mirror
// hub-header, HeaderCart and footer are kept as faithful mirror subtrees (B2 chrome stays MIRRORED per
// the B2 ruling); the css-1phsixa MAIN region (SupplierName + leftDock accordions + mainHeading +
// ProfileCenter map/info/tab-bar/product-list) is this real component.
//
// What is REAL here (the port, vs the dead mirror):
//   • the ProfileCenter product-type TAB BAR filters the visible product rows. Dead in the mirror
//     (stripped JS = R6-6 photocopy); live here via native event delegation (proven by behavior.mjs
//     `detail`). Each product row is tagged data-producttypes from LIVE's own sub-page grouping (the
//     seed JSON's per-listing `types`, detail-extract.mjs — zero inference); the clicked tab's token
//     shows only rows carrying it, "All" (token "*") shows everything.
//   • every product row is data-be-backed — the 5 live-matching suppliers are all BE-wired.
//
// B3.4 makes the per-product booking widget REAL (was PENDING-SYSTEM mirror in B3.3): the "Date Time"
// toggle, the −/+ quantity stepper and the "Add" button are wired via native DOM (same idiom as the tab
// bar) into the session cart store. "Add" reads the row's data-listing-id (the BE product documentId,
// stamped server-side by the listing join), qty, dates and prices, pushes a cart line, flips the button
// to "Added!", and the HeaderCart badge/tray update. The date-picker gate mask is removed — its default
// is the captured crawl-day value (= today), so the pixel gate stays green; behavior now drives it.
// Presentation still served from the faithful captured markup (absolute S3 logos) = PH2-RESKIN. Desktop
// only; mobile stays fully mirrored (F30). F35: the ACTIVITIES tab bar is a fixed template — see ledger.

import { useEffect, useMemo, useRef } from "react";
import { useCartStore } from "@/lib/cart-store";
import { dateGuard } from "@/lib/cart-billing";
import { NOT_SOLD_ONLINE } from "@/lib/purchase-truth";
import MarketplaceCartLayer from "./MarketplaceCartLayer";
import DatePickerLayer from "./DatePickerLayer";
import PublicProfileSections from "@/components/profile/PublicProfileSections";
import { HOST_PRESENTS } from "@/lib/profile-template";
import type { PublicProfile } from "@/lib/profile-template";

export type SupplierDetailData = {
  slug: string;
  variant: string;
  supplierSlug: string;
  supplierName: string;
  bodyClass: string;
  productTypes: string[];
  tabs: { label: string; token: string; matches: boolean; active: boolean }[];
  tabActiveClass: string;
  tabIdleClass: string;
  chrome: {
    headerClass: string;
    headerInner: string;
    cartClass: string;
    cartInner: string;
    footerClass: string;
    footerInner: string;
  };
  mainClass: string;
  mainInner: string;
  productCount: number;
  beBacked: number;
  untagged: number;
  publicProfile?: PublicProfile;
};

// mountain token for cart lines (all 5 detail pages are whistler; parsed from the slug for correctness).
function mountainOf(slug: string): string {
  const m = /^mountain-([a-z0-9-]+?)_/.exec(slug);
  return m?.[1] || "whistler";
}
const num = (s: string | null | undefined): number | null => {
  if (!s) return null;
  const v = /([\d,.]+)/.exec(s)?.[1];
  return v ? Number(v.replace(/,/g, "")) : null;
};

/**
 * RUN001 activation — a row may be ADDED only when the server stamped it purchasable (`data-purchasable="true"`, set by
 * listing-join.ts from the backend purchase truth) AND handed it the identity the booking needs (`data-listing-id`).
 * A row without both is OFF: it keeps its copy, prices and fields, and its Add is a disabled control that says so. The
 * former offline fallback (`mountain:supplier:key` line with no listing) is not an add path for an OFF row.
 */
export function rowPurchasable(li: Pick<Element, "getAttribute">): boolean {
  return li.getAttribute("data-purchasable") === "true" && !!(li.getAttribute("data-listing-id") ?? "").trim();
}

export default function MarketplaceSupplierDetail({ data }: { data: SupplierDetailData }) {
  const { chrome } = data;
  const mainRef = useRef<HTMLDivElement>(null);
  const mountain = mountainOf(data.slug);

  // The MAIN region is faithful mirror markup (dangerouslySetInnerHTML), memoized so its element
  // reference is STABLE — React never re-applies innerHTML, so the raw product/tab DOM keeps its
  // state across interactions (same pattern as the browse leftDock). Behavior is driven by native
  // DOM in the effect below, not React re-render, which also guarantees pixel parity with the mirror.
  const mainEl = useMemo(
    () => (
      <div
        className={data.mainClass}
        ref={mainRef}
        dangerouslySetInnerHTML={{ __html: data.mainInner }}
      />
    ),
    [data.mainClass, data.mainInner],
  );

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    // ProfileCenter_tabs is a hashed CSS-module class (ProfileCenter_tabs__xxxx) — match by prefix.
    const tabLis = Array.from(
      root.querySelectorAll<HTMLLIElement>('[class*="ProfileCenter_tabs"] li[data-tab-token]'),
    );
    const products = Array.from(
      root.querySelectorAll<HTMLLIElement>(".product-rental-list[data-producttypes]"),
    );
    if (!tabLis.length || !products.length) return;

    // "All" (token "*") shows every row — the mirror default, matching live's concatenated list. A
    // specific type tab shows one row PER UNIQUE product of that type: live re-renders its type tabs
    // to the deduped per-type set, and the "All" DOM lists a multi-type product once per type, so we
    // dedupe by data-product-key to match live's counts (verified live 2026-07-20). Booking widgets
    // inside each row stay mirror = PENDING-SYSTEM.
    const applyToken = (token: string) => {
      const seen = new Set<string>();
      for (const li of products) {
        if (token === "*") {
          li.style.display = "";
          continue;
        }
        const types = (li.getAttribute("data-producttypes") || "").split(" ").filter(Boolean);
        const key = li.getAttribute("data-product-key") || "";
        const visible = types.includes(token) && !seen.has(key);
        if (visible) seen.add(key);
        li.style.display = visible ? "" : "none";
      }
    };

    const onClick = (e: Event) => {
      const li = e.currentTarget as HTMLLIElement;
      const token = li.getAttribute("data-tab-token") || "*";
      for (const t of tabLis) t.className = t === li ? data.tabActiveClass : data.tabIdleClass;
      applyToken(token);
    };

    for (const li of tabLis) li.addEventListener("click", onClick);
    // initial DOM already reflects the mirror default (All active, all products shown) — no-op.
    return () => {
      for (const li of tabLis) li.removeEventListener("click", onClick);
    };
  }, [data.tabActiveClass, data.tabIdleClass]);

  // B3.4 — wire the per-product booking widgets (native DOM, JS enabled): the −/+ quantity stepper and
  // the "Add" button (the date field is already a live text input; on desktop — the only ported
  // viewport — Products_prodActions is display:flex, so the "Date Time" mobile disclosure is a no-op).
  // "Add" reads the row's BE listing id + fields and pushes a session-cart line; the button flips to
  // "Added!" and the HeaderCart badge/tray update. Buttons stay in sync with the store (remove reverts).
  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const rows = Array.from(
      root.querySelectorAll<HTMLLIElement>(".product-rental-list[data-producttypes]"),
    );
    if (!rows.length) return;

    const idOf = (li: HTMLLIElement) =>
      li.getAttribute("data-listing-id") ||
      `${mountain}:${data.supplierSlug}:${li.getAttribute("data-product-key") || ""}`;

    const readRow = (li: HTMLLIElement) => {
      const title = (li.querySelector('[class*="Products_prodHeading"]')?.textContent || "").trim();
      if (!title) return null;
      const strongs = Array.from(li.querySelectorAll('[class*="Products_pricing"] strong'));
      const dateInputs = Array.from(
        li.querySelectorAll<HTMLInputElement>(".react-datepicker__input-container input"),
      );
      const qtyInput = li.querySelector<HTMLInputElement>('[class*="Products_qtyContain"] input');
      // S35 · F224 — "marketplace Details must carry the PRODUCT's details: what it is, start TIME
      // where the product has one, plus booked date." All three are ALREADY IN THIS ROW and were simply
      // never carried onto the line:
      //   • what it is  — `Products_prodDesc`, live's own product copy.
      //   • start time  — the "Desired Time" <select>'s CURRENT VALUE. Activities have one; rentals do
      //     not, and a row without the control yields undefined rather than a fabricated time. That is
      //     the founder's "Snowmobile Family Tour — missing" case: the control was on the page, the
      //     value just never reached the cart.
      //   • booked date — the "Desired Date" input, which the line already carried as `startDate`; F224
      //     is about LABELLING it as the date it is booked FOR (see CartPage).
      const description = (li.querySelector('[class*="Products_prodDesc"]')?.textContent || "").trim();
      const timeSelect = li.querySelector<HTMLSelectElement>('[class*="Products_prodInput"] select');
      return {
        key: li.getAttribute("data-product-key") || "",
        listingId: li.getAttribute("data-listing-id"),
        title,
        description: description || undefined,
        startTime: timeSelect?.value || undefined,
        guestPrice: num(strongs[0]?.textContent),
        memberPrice: num((strongs[1] ?? strongs[0])?.textContent),
        qty: Math.max(1, Math.min(10, Number(qtyInput?.value) || 1)),
        startDate: dateInputs[0]?.value || "",
        endDate: dateInputs[1]?.value || "",
        mountain,
        supplier: data.supplierName,
        supplierSlug: data.supplierSlug,
      };
    };

    const setAdded = (li: HTMLLIElement, added: boolean) => {
      const btn = li.querySelector<HTMLButtonElement>('[class*="Products_btnWarning"]');
      if (!btn) return;
      // an OFF row's control keeps its "Not sold online" label; it is never an "Add" and never an "Added!"
      if (!rowPurchasable(li)) return;
      const textNode = Array.from(btn.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE && (n.nodeValue || "").trim(),
      );
      if (textNode) textNode.nodeValue = added ? "Added!" : "Add";
      btn.classList.toggle("Products_Active__9R5FG", added);
      btn.classList.toggle("notAllowed", added);
    };

    const syncAll = () => {
      const ids = new Set(useCartStore.getState().items.map((i) => i.id));
      for (const li of rows) setAdded(li, ids.has(idOf(li)));
    };

    const cleanups: (() => void)[] = [];
    for (const li of rows) {
      const qc = li.querySelector('[class*="Products_qtyContain"]');
      const qtyInput = qc?.querySelector<HTMLInputElement>("input");
      const cursors = qc ? Array.from(qc.querySelectorAll<HTMLElement>("span.cursor")) : [];
      const decEl = cursors[0];
      const incEl = cursors[cursors.length - 1];
      if (qtyInput && decEl && incEl && cursors.length >= 2) {
        const step = (delta: number) => {
          qtyInput.value = String(Math.max(1, Math.min(10, (Number(qtyInput.value) || 1) + delta)));
        };
        const dec = () => step(-1);
        const inc = () => step(1);
        decEl.addEventListener("click", dec);
        incEl.addEventListener("click", inc);
        cleanups.push(() => {
          decEl.removeEventListener("click", dec);
          incEl.removeEventListener("click", inc);
        });
      }
      const addBtn = li.querySelector<HTMLButtonElement>('[class*="Products_btnWarning"]');
      if (addBtn && !rowPurchasable(li)) {
        // RUN001 activation — OFF: the backend did not publish this product as purchasable here (or did not answer). The
        // control is disabled semantically (disabled + aria-disabled, so it is skipped by focus and inert to Enter/Space,
        // not merely greyed), labelled with the taxonomy word, and its click path is removed. Copy, prices and fields stay.
        const textNode = Array.from(addBtn.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && (n.nodeValue ?? "").trim(),
        );
        const priorLabel = textNode?.nodeValue ?? null;
        if (textNode) textNode.nodeValue = NOT_SOLD_ONLINE;
        addBtn.disabled = true;
        addBtn.setAttribute("aria-disabled", "true");
        addBtn.setAttribute("data-purchase-off", "");
        addBtn.classList.add("notAllowed");
        const refuse = (e: Event) => { e.preventDefault(); e.stopImmediatePropagation(); };
        addBtn.addEventListener("click", refuse, true);
        cleanups.push(() => {
          addBtn.removeEventListener("click", refuse, true);
          if (textNode && priorLabel !== null) textNode.nodeValue = priorLabel;
          addBtn.disabled = false;
          addBtn.removeAttribute("aria-disabled");
          addBtn.removeAttribute("data-purchase-off");
          addBtn.classList.remove("notAllowed");
        });
      } else if (addBtn) {
        // FOUNDER-WALK-QA-STAGING-007 · D1 — THE DATE GUARD THIS ROW NEVER HAD.
        //
        // `readRow` reads `dateInputs[0]?.value || ""` / `dateInputs[1]?.value || ""` and this handler
        // added whatever came back, unconditionally. The captured rental row ships its "Ending:" field
        // EMPTY (`value=""`), so the founder's own supplier page would put a HIRE in the cart with no
        // return date — and the button gave no sign anything was wrong.
        //
        // The rule is not written here: it is `dateGuard` in cart-billing, the same predicate
        // QuickViewLayer's Add gates on (F97). One rule, so the two add surfaces cannot disagree about
        // what a bookable date is.
        const dateInputs = Array.from(
          li.querySelectorAll<HTMLInputElement>(".react-datepicker__input-container input"),
        );
        // A row that RENDERS a second date field is booking a range (a hire); one that does not is a
        // single-day activity. Read off the row's own DOM rather than the page variant, so a row is
        // never asked for a date it does not offer.
        const requiresEnd = dateInputs.length >= 2;
        const hintEl = document.createElement("div");
        hintEl.setAttribute("data-detail-add-hint", "");
        hintEl.style.cssText =
          "font-size:11px;line-height:1.3;color:#b23b3b;margin-top:4px;max-width:190px;white-space:normal;";
        addBtn.insertAdjacentElement("afterend", hintEl);
        // Style only — the `notAllowed`/`Products_Active` classes stay owned by setAdded(), so the
        // guard and the "Added!" state cannot fight over the same class.
        const applyGuard = () => {
          const g = dateGuard({
            startDate: dateInputs[0]?.value ?? "",
            endDate: dateInputs[1]?.value ?? "",
            requiresEnd,
          });
          addBtn.disabled = !g.canAdd;
          addBtn.setAttribute("aria-disabled", String(!g.canAdd));
          addBtn.style.opacity = g.canAdd ? "" : "0.55";
          addBtn.style.cursor = g.canAdd ? "" : "not-allowed";
          hintEl.textContent = g.hint ?? "";
          hintEl.style.display = g.hint ? "" : "none";
          return g;
        };
        const onAdd = () => {
          // `disabled` already blocks a real click; the guards are re-read here so a programmatic
          // dispatch (the port harness drives these rows directly) cannot route around them either.
          if (!rowPurchasable(li)) return;
          if (!applyGuard().canAdd) return;
          const item = readRow(li);
          if (item) useCartStore.getState().addItem(item);
        };
        // The calendar popover writes its pick into this SAME input via writeInputValue, which
        // dispatches BOTH `input` and `change`; the field is also editable text. Listen for both so the
        // guard re-runs whether the date was picked or typed.
        const onDateChange = () => {
          applyGuard();
        };
        addBtn.addEventListener("click", onAdd);
        for (const d of dateInputs) {
          d.addEventListener("input", onDateChange);
          d.addEventListener("change", onDateChange);
        }
        applyGuard();
        cleanups.push(() => {
          addBtn.removeEventListener("click", onAdd);
          for (const d of dateInputs) {
            d.removeEventListener("input", onDateChange);
            d.removeEventListener("change", onDateChange);
          }
          hintEl.remove();
          addBtn.disabled = false;
          addBtn.removeAttribute("aria-disabled");
          addBtn.style.opacity = "";
          addBtn.style.cursor = "";
        });
      }
    }

    syncAll();
    const unsub = useCartStore.subscribe(syncAll);
    return () => {
      unsub();
      for (const c of cleanups) c();
    };
  }, [data.slug, data.supplierSlug, data.supplierName, mountain]);

  return (
    <div id="__next">
      <div className="__className_d275c8 mobileWidthSet">
        {/* MIRROR chrome — faithful hub header (sticky), HeaderCart section + footer stay MIRRORED (B2
            ruling), byte-identical for the pixel gate. The captured header cart button + badge are made
            real (open + count) by MarketplaceCartLayer below (F36, document-scoped). */}
        <header className={chrome.headerClass} dangerouslySetInnerHTML={{ __html: chrome.headerInner }} />
        <section className={chrome.cartClass} dangerouslySetInnerHTML={{ __html: chrome.cartInner }} />

        {/* REAL main region (css-1phsixa) — ProfileCenter profile with the live product-type tab bar. */}
        {mainEl}
        {/* PROFILE-DEDUPLICATE-DESIGN-FIT — the mirrored ProfileCenter above already carries the supplier's
            description, "Special Note:" with Email / Phone, and the product rows; the shared block adds only
            what the mirror lacks (HOST_PRESENTS.mirrorDetail). */}
        {data.publicProfile ? <section data-supplier-profile-information style={{ maxWidth: 980, margin: "0 auto", padding: "0 18px 34px" }}><PublicProfileSections profile={data.publicProfile} host={HOST_PRESENTS.mirrorDetail} /></section> : null}

        <footer
          className={chrome.footerClass || undefined}
          dangerouslySetInnerHTML={{ __html: chrome.footerInner }}
        />
      </div>
      {/* F36 — real cart badge + tray (booking widgets above stay detail-only). */}
      <MarketplaceCartLayer />
      {/* M5 (S5) — per-product date picker: wires the captured "Date Time" inputs to a real calendar
          whose pick fills the SAME input the cart already reads (no parallel path). Pixel-neutral at
          rest (portal renders nothing until a field is clicked). */}
      <DatePickerLayer />
    </div>
  );
}
